"use client";
import React, { useState, useEffect } from "react";
import Close from "../assets/icons/close";
import ChevronRight from "../assets/icons/chevron-right";
import ProfileIcon from "../assets/icons/profile-icon";
import ReferIcon from "../assets/icons/refer-icon";
import RewardsIcon from "../assets/icons/rewards-icon";
import NotificationIcon from "../assets/icons/notifications-icon";
import MessageNotifIcon from "../assets/icons/message-notification-icon";
import ContactIcon from "../assets/icons/contact-icon";
import LogoutIcon from "../assets/icons/logout-icon";
import UserIcon from "../assets/icons/user-icon";
import FingerprintBig from "../assets/icons/finerprint-big";
import useRequest from "../hooks/request.hook";
import { authService } from "@/app/api/service/auth";
import { initBranch, trackEvent } from "../utils/branchio";
import { getReleventDomain, removeXPartner } from "../utils/helper";
import Reports from "../assets/icons/reports";
import MilestoneMenuIcon from "../assets/icons/milestone-menu";
import { Check, RefreshCcw, X } from "lucide-react";
import {
    getUserNotificationPreference,
    PREF_KEY,
    setUserNotificationPreference,
} from "../utils/notificationpreference";
import NotificationBlockedDialog from "./popup/notification-blocked";
import LoadingIcon from "../assets/icons/loading";
import { setAnalyticsEvent } from "../utils/SetAnalyticsEvent";

interface MenuItem {
    title: string;
    icon?: JSX.Element;
    link?: string;
    branchEvent?: string;
    children?: MenuItem[];
    toggle?: boolean;
}

interface SideDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    position?: "left" | "right";
    name: string | undefined;
    email: string | undefined;
    phone: string | undefined;
    userId: string | undefined;
}

const getRelevantIcon = () => {
    if (isIOS()) {
        return <X />;
    }

    const userPref = getUserNotificationPreference();
    return userPref !== null ? (
        userPref === "denied" ? (
            <X />
        ) : (
            <Check />
        )
    ) : (
        <span>
            <RefreshCcw />
        </span>
    );
};

const menuItems: MenuItem[] = [
    {
        title: "Milestone",
        icon: <MilestoneMenuIcon />,
        link: `${process.env.ICM_URL}/milestone`,
        branchEvent: "Miltestone_Profilesidebar_Click",
    },
    { title: "Profile", icon: <UserIcon />, link: `${process.env.ICM_URL}/profile` },
    {
        title: "Refer & Earn",
        icon: <ReferIcon />,
        link: `${process.env.ICM_URL}/refer`,
        branchEvent: "More_Refer",
    },
    { title: "Rewards", icon: <RewardsIcon />, link: `${process.env.ICM_URL}/refer?rewards` },
    { title: "Reports", icon: <Reports />, link: `${process.env.ICM_URL}/incredmoney-reports` },
    {
        title: "Notification Preferences",
        icon: <NotificationIcon />,
        children: [
            {
                title: "WhatsApp Notifications",
                link: "",
                icon: <MessageNotifIcon />,
                toggle: true,
            },
            {
                title: "Browser Notifications",
                link: "",
                icon: <></>,
                toggle: false,
            },
        ],
    },
    { title: "Contact Us", icon: <ContactIcon />, link: `${process.env.ICM_URL}/contact` },
    {
        title: "About",
        icon: <FingerprintBig />,
        children: [
            { title: "About Us", link: `${process.env.ICM_URL}/about` },
            { title: "Terms & Conditions", link: `${process.env.ICM_URL}/terms-of-use` },
            { title: "Privacy Policy", link: `${process.env.ICM_URL}/privacy-policy` },
            { title: "Disclaimer", link: `${process.env.ICM_URL}/disclaimer` },
        ],
    },
    { title: "Logout", icon: <LogoutIcon />, link: "" },
];

function isIOS(): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
        return false;
    }

    // Check using userAgent only, avoiding deprecated platform property
    return /iPad|iPhone|iPod|Mac|Macintosh|Darwin/i.test(navigator.userAgent);
}

function SideDrawer({
    isOpen,
    onClose,
    position = "right",
    name,
    email,
    phone,
    userId,
}: SideDrawerProps) {
    const currentDomain = getReleventDomain();
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [toggles, setToggles] = useState<{ [key: string]: boolean }>({});
    const { request } = useRequest(process.env.API_BASE_URL);
    const [isNotificationBlocked, setShowNotificationBlocked] = useState(false);
    const [browserNotifIcon, setBrowserNotifIcon] = useState<JSX.Element | null>(null);

    useEffect(() => {
        setBrowserNotifIcon(getRelevantIcon());
    }, []);

    // Check browser notification permissions
    useEffect(() => {
        if (isIOS()) {
            // iOS does not support browser notifications in the same way
            console.warn("Browser notifications are not supported on iOS.");
            return;
        }

        if (!("Notification" in window)) {
            // Browser doesn't support notifications
            setUserNotificationPreference("denied");
            return;
        }

        // Only check the current permission status without requesting
        const currentPermission = Notification.permission;
        const storagePref = localStorage.getItem(PREF_KEY);
        if (storagePref !== "default" && storagePref !== "denied") {
            setUserNotificationPreference(currentPermission);
        }

        // Initialize the toggle state based on current permission
        setToggles((prev) => ({
            ...prev,
            browserNotifications: currentPermission === "granted",
        }));
    }, []);

    // Add effect to disable body scroll when drawer is open
    useEffect(() => {
        if (isOpen) {
            // Save current scroll position and disable scrolling
            const scrollY = window.scrollY;
            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = "100%";
            document.body.style.overflowY = "hidden";
        } else {
            // Re-enable scrolling and restore position
            const scrollY = document.body.style.top;
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.style.overflowY = "";
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY.replace("-", "")) || 0);
            }
        }

        return () => {
            // Cleanup function to ensure scroll is re-enabled if component unmounts
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.width = "";
            document.body.style.overflowY = "";
        };
    }, [isOpen]);

    const handleToggle = (title: string) => {
        setActiveSection((prev) => (prev === title ? null : title));
    };

    const handleClick = (link: string | undefined, branchEvent?: string | undefined) => {
        if (branchEvent) {
            initBranch()
            .then(() => {
                    setAnalyticsEvent("", branchEvent);
                    trackEvent(branchEvent, email || "", phone || "", userId || "");
                    // Only navigate after tracking event with a delay
                    if (link) {
                        setTimeout(() => {
                            window.location.href = link;
                        }, 500);
                    }
                })
                .catch((error) => {
                    console.error("Error initializing Branch:", error);
                    // Navigate even if branch tracking fails
                    if (link) {
                        window.location.href = link;
                    }
                });
        } else if (link) {
            // Direct navigation if no branch event
            window.location.href = link;
        }
    };

    const handleLogout = () => {
        authService
            .logout()
            .then(() => {
                // window.location.href = `${process.env.ICM_URL}/login`;
                removeXPartner();
                window.location.href = currentDomain + "/login";
            })
            .catch((error) => {
                console.error("Logout failed:", error);
            });
    };

    const handleToggleSwitch = async (type: string, currentState: boolean) => {
        const newState = !currentState;
        setToggles((prev) => ({ ...prev, [type]: newState }));
        await recordConsent(type, newState);
    };

    const recordConsent = async (type: string, consent: boolean) => {
        const payload = {
            type,
            consent,
        };
        try {
            const response = await request({
                url: "/users/setConsent",
                method: "POST",
                data: payload,
            });
            console.log("Consent updated successfully:", response.data);
        } catch (error) {
            console.error("Error updating consent:", error);
        }
    };

    const invokeNotificationPermission = () => {
        if (isIOS()) {
            // iOS does not support browser notifications in the same way
            console.warn("Browser notifications are not supported on iOS.");
            return;
        }

        if (!("Notification" in window)) {
            console.warn("This browser does not support notifications.");
            return;
        }

        if (Notification.permission === "default") {
            setBrowserNotifIcon(<LoadingIcon className="animate-spin" />);
            Notification.requestPermission()
                .then((permission) => {
                    // Update UI based on permission result
                    setUserNotificationPreference(permission);
                    setToggles((prev) => ({
                        ...prev,
                        browserNotifications: permission === "granted",
                    }));
                    setBrowserNotifIcon(getRelevantIcon());

                    if (permission !== "granted") {
                        setShowNotificationBlocked(true);
                    }
                })
                .catch((error) => {
                    console.error("Error requesting notification permission:", error);
                    setUserNotificationPreference("denied");
                });
            return;
        }

        if (getUserNotificationPreference() === "denied") {
            setShowNotificationBlocked(true);
            return;
        }

        if (Notification.permission === "granted") {
            // Notifications are already enabled
            setUserNotificationPreference("granted");
            return;
        }

        Notification.requestPermission().then((permission) => {
            setUserNotificationPreference(permission === "granted" ? "granted" : "denied");
        });
    };

    return (
        <>
            <div
                className={`fixed inset-0 z-50 bg-gray-800 bg-opacity-80 transition-opacity ${
                    isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
                }`}
                onClick={onClose}
            ></div>

            <div
                className={`fixed top-0 z-50 ${
                    position === "right" ? "right-0" : "left-0"
                } h-full transform bg-white shadow-lg transition-transform ${
                    isOpen
                        ? "translate-x-0"
                        : position === "right"
                          ? "translate-x-full"
                          : "-translate-x-full"
                } flex w-4/5 flex-col md:w-1/4`} // Added flex flex-col
            >
                <div className="relative h-auto w-full flex-shrink-0 bg-brand-primary-500 p-5">
                    {" "}
                    {/* Added flex-shrink-0 */}
                    <div className="flex items-center gap-4">
                        <ProfileIcon />
                        <div className="flex flex-col">
                            <p className="mb-0 text-[20px] font-semibold text-white sm:text-[18px]">
                                {name}
                            </p>
                            <p className="text-sm font-normal text-white sm:text-sm">
                                Profile Details
                            </p>
                        </div>
                    </div>
                    <Close
                        className="absolute right-4 top-4 cursor-pointer text-white"
                        onClick={onClose}
                    />
                </div>

                <div className="flex-1 overflow-y-auto">
                    {" "}
                    {/* Changed to flex-1 */}
                    <div className="h-full">
                        {menuItems.map((item, index) => (
                            <div key={index} className="cursor-pointer">
                                {item.children ? (
                                    <>
                                        <div
                                            className="flex items-center justify-between px-5 py-4 text-gray-700 hover:bg-slate-50"
                                            onClick={() => handleToggle(item.title)}
                                        >
                                            <div className="flex items-center gap-4">
                                                {item.icon}
                                                <span className="text-base font-medium text-black">
                                                    {item.title}
                                                </span>
                                            </div>
                                            <span
                                                className={`transform transition-transform duration-300 ${
                                                    activeSection === item.title
                                                        ? "rotate-90"
                                                        : "rotate-0"
                                                }`}
                                            >
                                                <ChevronRight />
                                            </span>
                                        </div>

                                        <div
                                            className={`transition-max-height overflow-hidden duration-300 ease-in-out ${
                                                activeSection === item.title
                                                    ? "max-h-[500px]"
                                                    : "max-h-0"
                                            }`}
                                        >
                                            {item.children.map((child, childIndex) => (
                                                <div
                                                    key={childIndex}
                                                    onClick={() => {
                                                        if (
                                                            item.title != "WhatsApp Notifications"
                                                        ) {
                                                            handleClick(child.link);
                                                        }

                                                        if (
                                                            child.title === "Browser Notifications"
                                                        ) {
                                                            invokeNotificationPermission();
                                                        }
                                                    }}
                                                    className="flex items-center justify-between gap-4 px-10 py-3 text-gray-700 hover:bg-slate-50"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        {child.title !== "Browser Notifications"
                                                            ? child.icon
                                                            : browserNotifIcon}
                                                        <span className="text-xs font-medium text-black md:text-base">
                                                            {child.title}
                                                        </span>
                                                    </div>
                                                    {child.toggle && (
                                                        <button
                                                            onClick={() =>
                                                                handleToggleSwitch(
                                                                    "whatsapp",
                                                                    toggles["whatsapp"] || false,
                                                                )
                                                            }
                                                            className={`flex h-6 w-10 items-center rounded-full px-1 ${
                                                                toggles["whatsapp"]
                                                                    ? "justify-end bg-green-500"
                                                                    : "justify-start bg-gray-300"
                                                            }`}
                                                        >
                                                            <div className="h-4 w-4 rounded-full bg-white"></div>
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        className="flex items-center gap-4 px-5 py-4 text-gray-700 hover:bg-slate-50"
                                        onClick={() =>
                                            item.title === "Logout"
                                                ? handleLogout()
                                                : handleClick(item.link, item.branchEvent)
                                        }
                                    >
                                        {item.icon}
                                        <span className="text-base font-medium text-black">
                                            {item.title}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {isNotificationBlocked && (
                <NotificationBlockedDialog
                    open={isNotificationBlocked}
                    onAccept={() => setShowNotificationBlocked(false)}
                />
            )}
        </>
    );
}

export default SideDrawer;
